import { CONSTANTS } from 'config/constants';
import Cookies from 'js-cookie';
import { Order } from 'models/order';
import { Organization } from 'models/organization';
import { handleDownload } from 'utils/file';
import { patientDisplayName } from 'utils/orders';

const fetchAndDownload = async (uri: string, fileName: string, init?: RequestInit): Promise<void> => {
    const res = await fetch(uri, {
        ...init,
        headers: {
            authorization: Cookies.get(CONSTANTS.TOKEN_KEY) || '',
            ...init?.headers,
        },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const error: Error & { graphQLErrors?: unknown[] } = new Error(body?.message || '');
        // Reshape a USER_FRONTEND_FEEDBACK response into the structure the denthub error
        // boundary recognises, so callers can route it to the feedback dialog.
        if (body?.type === 'USER_FRONTEND_FEEDBACK') {
            error.graphQLErrors = [{ extensions: { exception: { type: body.type, otherInfo: body.otherInfo } } }];
        }
        throw error;
    }
    const blob = await res.blob();
    return handleDownload(fileName, blob);
};

/**
 * `<orderId>[_<internalOrderId>][_<patient name>]_<date><suffix>`, e.g. suffix
 * `_work.pdf`.
 *
 * DD-3620: the patient segment is dropped when the server did not send a patient.
 * That is the case for a restricted external user, whose downloads must not carry
 * the patient's name — and the name also travels in the request URL, so building
 * it from whatever arrived is what keeps it out of both. The decision is the
 * data's, not a role check: the frontend renders what the backend returns.
 */
export const buildOrderPdfFileName = (order: Order, fileNameSuffix: string): string => {
    // A patient-less order (DD-3494) omits the patient segment.
    const patientName = patientDisplayName(order.patient) || undefined;
    const currentDate = new Date().toLocaleDateString();
    const baseFileName = [order.orderId, order.internalOrderId, patientName, currentDate].filter(Boolean).join('_');
    return `${baseFileName}${fileNameSuffix}`;
};

export const downloadOrderPdf = async (orderId: string, type: string, fileName: string): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/${orderId}/${type}/${encodeURIComponent(fileName)}`;
    return fetchAndDownload(uri, fileName);
};

export const downloadOrderReceiptPdf = async (order: Order): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/${order._id}/order-receipt`;
    return fetchAndDownload(uri, `Receipt_${order.orderId}.pdf`);
};

export const downloadDestinationAddressLabelPdf = async (order: Order): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/${order._id}/destination-address-label`;
    return fetchAndDownload(uri, `etikett_${(order.deliveryDetails.destination as Organization).name}.pdf`);
};

export const downloadPatientLabelPdf = async (order: Order): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/${order._id}/patient-label`;
    return fetchAndDownload(uri, `etikett_${order.orderId}.pdf`);
};

export const downloadDeadlineSchedulePdf = async (
    date: Date,
    fileName: string,
    clinicId?: string,
    departmentId?: string,
): Promise<void> => {
    const departmentQuery = departmentId ? `?departmentId=${encodeURIComponent(departmentId)}` : '';
    const uri = `${CONSTANTS.DOC_API_URI}/documents/deadline-schedule/${date}/${clinicId}/${encodeURIComponent(fileName)}${departmentQuery}`;
    return fetchAndDownload(uri, fileName);
};

export const downloadMergeOrderPdf = async (orderIds: string[], type: string, fileName: string): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/orders/merge`;
    return fetchAndDownload(uri, fileName, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds, type, fileName }),
    });
};

export const downloadDocumentBundlePdf = async (
    orderId: string,
    documents: { documentType: string; copies: number }[],
    fileName: string,
): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/orders/${orderId}/bundle`;
    return fetchAndDownload(uri, fileName, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documents, fileName }),
    });
};

export const downloadMaterialListPdf = async (fileName: string): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/material/material-list`;
    return fetchAndDownload(uri, fileName);
};

export const downloadMaterialPriceListPdf = async (fileName: string, priceListId: string): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/lab/material/price-list/${priceListId}`;
    return fetchAndDownload(uri, fileName);
};

export const downloadActionPriceListPdf = async (fileName: string, priceListId: string): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/lab/action/price-list/${priceListId}`;
    return fetchAndDownload(uri, fileName);
};

export const downloadClientListPdf = async (fileName: string): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/client/client-list`;
    return fetchAndDownload(uri, fileName);
};

export const downloadActionBasedClientActionPriceListPdf = async (
    fileName: string,
    clientId: string,
): Promise<void> => {
    const uri = `${CONSTANTS.DOC_API_URI}/documents/client/action-based/action-price-list/${clientId}`;
    return fetchAndDownload(uri, fileName);
};
